import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Unlock } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { useScrollToHash } from '@/hooks/useScrollToHash';
import { formatDate, formatDateTime, lastMonth, type Period } from '@/lib/dates';
import {
  Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, PeriodPicker, Spinner,
} from '@/features/common/ui';

interface PeriodLock {
  id: string;
  fromDate: string;
  toDate: string;
  reason: string;
  unlockedAt: string | null;
  unlockReason: string | null;
  createdAt: string;
  isActive: boolean;
}

/**
 * Freeze a filed period, and reopen one.
 *
 * ── WHY THIS IS THE MOST IMPORTANT CONTROL ON THIS PAGE ──
 * Without it, a backdated invoice can land in a month whose return has already
 * gone in, and nothing says so. That is the single thing most likely to make a
 * CA distrust every other figure in the app. Locking is what makes the returns
 * mean something.
 *
 * A reopened lock is kept and stamped rather than deleted, and the list shows
 * both - that a period was locked and then deliberately reopened by a named
 * person is exactly the kind of fact an auditor asks about.
 */
export function PeriodLocksCard() {
  const org = useActiveOrg();
  const perms = usePermissions();
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState<Period>(() => lastMonth());
  const [reason, setReason] = useState('');

  const locks = useQuery({
    queryKey: ['period-locks', org?.id],
    queryFn: () => api.get<{ items: PeriodLock[] }>('/periods/locks'),
    enabled: !!org?.id,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['period-locks', org?.id] });
    void queryClient.invalidateQueries({ queryKey: ['review', org?.id] });
    void queryClient.invalidateQueries({ queryKey: ['practice'] });
  };

  const lock = useMutation({
    mutationFn: () => api.post('/periods/locks', { from: period.from, to: period.to, reason: reason.trim() }),
    onSuccess: () => {
      setReason('');
      invalidate();
    },
  });

  const unlock = useMutation({
    mutationFn: ({ id, why }: { id: string; why: string }) =>
      api.post(`/periods/locks/${id}/unlock`, { reason: why }),
    onSuccess: invalidate,
  });

  // The review's "Open the locks" links to /settings#period-locks.
  useScrollToHash('period-locks', !locks.isLoading);
  const canManage = perms.can('periodLock', 'manage');
  const error = lock.error instanceof ApiError ? lock.error : undefined;

  return (
    <Card id="period-locks" className="scroll-mt-4">
      <CardHeader>
        <CardTitle>Filed periods</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Locking a period refuses every invoice, expense and bank entry dated inside it - including
          an edit that would move one out. That is what keeps a filed return true.
        </p>

        {canManage ? (
          <form
            className="space-y-3 rounded-md border border-border p-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (reason.trim()) lock.mutate();
            }}
          >
            <PeriodPicker value={period} onChange={setPeriod} id="lock-period" />
            <Input
              label="Why"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="GSTR-1 filed for August 2026"
              error={error?.fieldError('reason')}
              aria-describedby="lock-reason-help"
            />
            <p id="lock-reason-help" className="text-xs text-muted-foreground">
              This is what somebody reads when their edit is refused. &ldquo;August is locked&rdquo;
              is not actionable; naming the return is.
            </p>
            {error && !error.fieldError('reason') ? (
              <p className="text-sm text-destructive">{error.message}</p>
            ) : null}
            <Button type="submit" size="sm" disabled={!reason.trim() || lock.isPending}>
              <Lock className="size-4" aria-hidden />
              Lock {period.label}
            </Button>
          </form>
        ) : null}

        {locks.isLoading ? (
          <Spinner />
        ) : locks.data?.items.length ? (
          <ul className="space-y-2">
            {locks.data.items.map((entry) => (
              <li key={entry.id} className="flex flex-wrap items-start justify-between gap-2 rounded-md border border-border p-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {formatDate(entry.fromDate)} to {formatDate(entry.toDate)}
                  </p>
                  <p className="text-xs text-muted-foreground">{entry.reason}</p>
                  {entry.unlockedAt ? (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Reopened {formatDateTime(entry.unlockedAt)}
                      {entry.unlockReason ? ` — ${entry.unlockReason}` : ''}
                    </p>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Badge variant={entry.isActive ? 'danger' : 'neutral'}>
                    {entry.isActive ? 'Locked' : 'Reopened'}
                  </Badge>
                  {entry.isActive && canManage ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={unlock.isPending}
                      onClick={() => {
                        const why = window.prompt('Why is this period being reopened?');
                        if (why?.trim()) unlock.mutate({ id: entry.id, why: why.trim() });
                      }}
                    >
                      <Unlock className="size-4" aria-hidden />
                      Reopen
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">Nothing has been locked yet.</p>
        )}
      </CardContent>
    </Card>
  );
}
